import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const EventDetailsModal = ({ 
  event, 
  onClose, 
  onRegister, 
  onExpertProfile, 
  userTier,
  isRegistered = false 
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!event) return null;

  const isEligible = event?.tierRequirement === 'All' || 
    (userTier === 'Premium' && ['Premium', 'All Access', 'Flex', 'Intro']?.includes(event?.tierRequirement)) ||
    (userTier === 'All Access' && ['All Access', 'Flex', 'Intro']?.includes(event?.tierRequirement)) ||
    (userTier === 'Flex' && ['Flex', 'Intro']?.includes(event?.tierRequirement)) ||
    (userTier === 'Intro' && event?.tierRequirement === 'Intro');

  const formatEventDate = (dateTime) => {
    const date = new Date(dateTime);
    return {
      date: date?.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric',
        month: 'long', 
        day: 'numeric' 
      }),
      time: date?.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      })
    };
  };

  const { date, time } = formatEventDate(event?.dateTime);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'agenda', label: 'Agenda', icon: 'List' },
    { id: 'expert', label: 'Expert', icon: 'User' },
    { id: 'materials', label: 'Materials', icon: 'FileText' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div>
              <h4 className="font-heading font-medium text-foreground mb-3">Description</h4>
              <p className="text-muted-foreground leading-relaxed">
                {event?.fullDescription || event?.description}
              </p>
            </div>
            <div>
              <h4 className="font-heading font-medium text-foreground mb-3">What You'll Learn</h4>
              <ul className="space-y-2">
                {event?.learningObjectives?.map((objective, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{objective}</span>
                  </li>
                )) || (
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">Comprehensive understanding of wellness principles</span>
                  </li>
                )}
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-medium text-foreground mb-3">Prerequisites</h4>
              <p className="text-muted-foreground">
                {event?.prerequisites || 'No prerequisites required. Open to all wellness levels.'}
              </p>
            </div>
          </div>
        );

      case 'agenda':
        return (
          <div className="space-y-4">
            <h4 className="font-heading font-medium text-foreground mb-3">Event Schedule</h4>
            {event?.agenda?.map((item, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 bg-muted/30 rounded-lg">
                <div className="text-sm font-medium text-primary min-w-[60px]">
                  {item?.time}
                </div>
                <div className="flex-1">
                  <h5 className="font-medium text-foreground mb-1">{item?.title}</h5>
                  <p className="text-sm text-muted-foreground">{item?.description}</p>
                </div>
              </div>
            )) || (
              <div className="text-center py-8">
                <Icon name="Clock" size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Detailed agenda will be shared with registered participants.</p>
              </div>
            )}
          </div>
        );

      case 'expert':
        return (
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                <Image
                  src={event?.expert?.profileImage}
                  alt={event?.expert?.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h4 className="font-heading font-medium text-foreground mb-1">
                  {event?.expert?.name}
                </h4>
                <p className="text-muted-foreground mb-2">{event?.expert?.credentials}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onExpertProfile(event?.expert)}
                >
                  View Full Profile
                </Button>
              </div>
            </div>
            <div>
              <h4 className="font-heading font-medium text-foreground mb-3">About the Expert</h4>
              <p className="text-muted-foreground leading-relaxed">
                {event?.expert?.bio}
              </p>
            </div>
            <div>
              <h4 className="font-heading font-medium text-foreground mb-3">Specializations</h4>
              <div className="flex flex-wrap gap-2">
                {event?.expert?.specializations?.map((spec, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                  >
                    {spec}
                  </span>
                ))}
              </div>
            </div>
          </div>
        );

      case 'materials':
        return (
          <div className="space-y-6">
            <div>
              <h4 className="font-heading font-medium text-foreground mb-3">Included Materials</h4>
              <div className="space-y-3">
                {event?.materials?.map((material, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                    <Icon name="FileText" size={16} className="text-primary" />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{material?.title}</p>
                      <p className="text-sm text-muted-foreground">{material?.description}</p>
                    </div>
                    {material?.available && (
                      <Icon name="Check" size={16} className="text-success" />
                    )}
                  </div>
                )) || (
                  <div className="text-center py-8">
                    <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Materials will be available after registration.</p>
                  </div>
                )}
              </div>
            </div>
            {event?.hasRecording && (
              <div className="p-4 bg-accent/10 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Video" size={16} className="text-accent" />
                  <span className="font-medium text-accent-foreground">Recording Available</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  This session will be recorded and available for {event?.tierRequirement} members for 30 days.
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-wellness-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="relative">
          <div className="h-64 overflow-hidden">
            <Image
              src={event?.image}
              alt={event?.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute top-4 right-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="bg-black/20 text-white hover:bg-black/40"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="bg-black/60 backdrop-blur-sm rounded-lg p-4 text-white">
              <h2 className="font-heading font-semibold text-xl mb-2">{event?.title}</h2>
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={14} />
                  <span>{date}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={14} />
                  <span>{time}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name={event?.type === 'Virtual' ? 'Video' : 'MapPin'} size={14} />
                  <span>{event?.type}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col h-[calc(90vh-16rem)]">
          {/* Tabs */}
          <div className="flex border-b border-border">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-wellness ${
                  activeTab === tab?.id
                    ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {renderTabContent()}
          </div>

          {/* Footer */}
          <div className="border-t border-border p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-sm text-muted-foreground">Price</div>
                  <div className="font-semibold text-foreground">
                    {event?.price > 0 ? `$${event?.price}` : 'Free'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground">Capacity</div>
                  <div className="font-semibold text-foreground">
                    {event?.registered}/{event?.capacity}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground">Duration</div>
                  <div className="font-semibold text-foreground">{event?.duration}</div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                {!isEligible && (
                  <div className="text-sm text-error">
                    Requires {event?.tierRequirement} membership
                  </div>
                )}
                
                {isRegistered ? (
                  <Button variant="default" disabled>
                    <Icon name="Check" size={16} className="mr-2" />
                    Registered
                  </Button>
                ) : (
                  <Button
                    variant={isEligible && event?.status !== 'Full' ? 'default' : 'outline'}
                    onClick={() => onRegister(event)}
                    disabled={!isEligible || event?.status === 'Full'}
                    iconName={!isEligible ? 'Lock' : event?.status === 'Full' ? 'X' : 'Calendar'}
                    iconPosition="left"
                  >
                    {!isEligible ? 'Upgrade Required' : 
                     event?.status === 'Full' ? 'Event Full' : 'Register Now'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsModal;